import React, {
	useEffect,
	useMemo, useRef,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import Socket from '../../connection/socket';
import { GreenroomState as GS } from '../../stores/types/types';
import { ROOM_STATES } from '../../stores/reducers/settings';
import { BROADCAST_STATES, BroadcastWarningTypes } from '../../stores/reducers/broadcasting';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import { BROADCAST_TEXT, RECORDING_OPTIONS } from './constants';
import useBroadcastState from './hooks/useBroadcastState';
import { appLogger } from '../../utils/datadog';
import * as BroadcastActions from '../../stores/actions/broadcasting';
import * as SessionActions from '../../stores/actions/session';
import { META_MENU_ROUTES, META_MENU_STATES, ShowSession } from '../../stores/reducers/session';
import BroadcastModal from './broadcast-modal';
import LiveCounter from '../general-components/live-counter';
import useBroadcastAlerts from './hooks/useBroadcastAlerts';
import { showAlert } from '../general-components/alert-service';
import * as Alerts from '../general-components/alert-service';
import BroadcastDropdown from './broadcast-dropdown';
import { ReactComponent as DropdownLivestream } from '../../images/icons/dropdown-livestream.svg';
import { ReactComponent as DropdownRecord } from '../../images/icons/dropdown-record.svg';
import { ReactComponent as DropdownShowbuild } from '../../images/icons/dropdown-showbuild.svg';
import LoadingSpinner from '../general-components/loading-spinner';
import * as ModalActions from '../../stores/actions/modal';
import { MODAL_TEMPLATES } from '../../stores/reducers/modal';
import Portal from '../general-components/portal';
import {
	LiveStreamConfirmationModalProps,
	LiveStreamStart,
} from '../modal/templates/live-stream-confirmation';
import { withinUnsafeTimeLeft } from '../../utils/scheduled-streaming';
import { PopoverStatus } from '../general-components/popover';

export interface BroadcastWarningMessage {
	type: BroadcastWarningTypes;
	message: string;
	description: string;
}

interface BroadcastControlsProps {
	socket: Socket | null;
}

export interface BroadcastOption {
	value: string;
	dropdownTitle: string;
	dropdownSubtitle: string;
	dropdownIcon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
	dropdownLabel: string;
	number?: number;
	startIcon?: string;
	startIconColor?: string;
	startLabel?: string;
	progressLabel?: string
	endLabel?: string;
}

export const BROADCAST_OPTIONS = {
	STREAM: 'stream',
	RECORD: 'record',
	EDIT: 'showbuilder',
};

export const broadcastOptions = [
	{
		icon: ICONS.STREAM,
		label: 'Stream',
		value: BROADCAST_OPTIONS.STREAM,
		className: 'broadcast-option-stream',
		dropdownTitle: 'Stream',
		dropdownSubtitle: 'Take it live',
		dropdownIcon: DropdownLivestream,
		dropdownLabel: 'Livestream',
		number: 0,
		startIcon: ICONS.STREAM_PLAY,
		startIconColor: COLORS.BLACK,
		startLabel: 'Stream',
		progressLabel: 'Streaming',
		endLabel: 'Stream',
	},
	{
		icon: ICONS.RECORD,
		label: 'Record only',
		shortLabel: 'Record',
		value: BROADCAST_OPTIONS.RECORD,
		className: 'broadcast-option-record',
		number: 1,
		dropdownTitle: 'Record',
		dropdownSubtitle: 'Create content',
		dropdownIcon: DropdownRecord,
		dropdownLabel: 'Recorder',
		startIcon: ICONS.STREAM_RECORD,
		startIconColor: COLORS.WHITE,
		startLabel: 'Record',
		progressLabel: 'Recording',
		endLabel: 'Recording',
	},
	{
		icon: ICONS.EDIT,
		label: 'Showbuilder',
		value: BROADCAST_OPTIONS.EDIT,
		className: 'broadcast-option-showbuilder',
		dropdownTitle: 'Showbuild',
		dropdownSubtitle: 'Plan the show',
		dropdownIcon: DropdownShowbuild,
		dropdownLabel: 'Showbuilder',
	},
];

const broadcastWarningMessages: BroadcastWarningMessage[] = [
	{
		type: BroadcastWarningTypes.recordingStartFailure,
		message: 'Recording Failed',
		description:
			'Greenroom could not initiate your recording. Your stream is not otherwise affected.',
	},
];

let startupTimer: NodeJS.Timeout;

const {
	STARTING_UP,
	READY,
	BROADCAST_STARTED,
	BROADCAST_STOPPED,
	BROADCAST_STARTING,
	BROADCAST_PREWARMING,
} = BROADCAST_STATES;

export default function BroadcastControls(props: BroadcastControlsProps) {
	const { socket } = props;

	const host = useSelector((s: GS) => s.SessionReducer.host);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const backstageCommsActive = useSelector((s: GS) => s.SettingsReducer.backstageCommsActive);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const streamState = useSelector((s: GS) => s.BroadcastReducer.streamState);
	const serverState = useSelector((s: GS) => s.BroadcastReducer.serverState);
	const startingBroadcast = useSelector((s: GS) => s.BroadcastReducer.startingBroadcast);
	const stoppingBroadcast = useSelector((s: GS) => s.BroadcastReducer.stoppingBroadcast);
	const goLiveButtonClicked = useSelector((s: GS) => s.BroadcastReducer.goLiveButtonClicked);
	const previousServerState = useSelector((s: GS) => s.BroadcastReducer.previousServerState);
	const goLiveFromModal = useSelector((s: GS) => s.BroadcastReducer.goLiveFromModal);
	const warningModalSent = useSelector((s: GS) => s.SessionReducer.warningModalSent);
	const broadcastWarnings = useSelector((s: GS) => s.BroadcastReducer.broadcastWarnings);
	const selectedRecordingOption = useSelector(
		(s: GS) => s.BroadcastReducer.selectedRecordingOption,
	);
	const scheduledStream = useSelector((s: GS) => s.MeetingReducer.scheduledStream);

	const { startStopBroadcast, getBroadcastState } = useBroadcastState();
	const { handleTimeAlerts } = useBroadcastAlerts();
	const dispatch = useDispatch();

	const streamStateRef = useRef<number>(streamState);
	const nextShowSession = useRef(undefined as ShowSession | undefined);
	const modeLineRef = useRef<HTMLDivElement>(null);

	const [mouseIsOver, setMouseIsOver] = useState(false);
	const [mockStreamStarting, setMockStreamStarting] = useState(false);
	const [mockStreamStopping, setMockStreamStopping] = useState(false);
	const [showBroadcastModal, setShowBroadcastModal] = useState(false);
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
	const [popoverStatus, setPopoverStatus] = useState<PopoverStatus>(PopoverStatus.Unset);

	useEffect(() => {
		streamStateRef.current = streamState;
	}, [streamState]);

	useEffect(() => {
		setMockStreamStarting(false);
		if (serverState === BROADCAST_STOPPED) {
			setMockStreamStopping(false);
		}
	}, [serverState]);

	useEffect(() => () => {
		// if the cooldown phase is still going on and this component has de-rendered, stop the timers
		clearInterval(startupTimer);
	}, []);

	// show the warnings
	useEffect(() => {
		if (broadcastWarnings && broadcastWarnings.length > 0) {
			for (let u = 0; u < broadcastWarnings.length; u += 1) {
				const broadcastWarning = broadcastWarnings[u];

				const warningMessage = broadcastWarningMessages.find(
					(warningMesssage) => warningMesssage.type === broadcastWarning,
				);

				if (!warningMessage) {
					// eslint-disable-next-line no-console
					console.warn(
						`Tried to warning user about something but unable to find error message: ${JSON.stringify(
							broadcastWarning,
						)}`,
					);
				} else {
					const { message, description } = warningMessage;
					setTimeout(
						() => showAlert({
							message,
							description,
							onClick: () => { },
							duration: 6000,
							type: 'warning',
						}),
						1000,
					);
				}
			}
		}
	}, [broadcastWarnings]);

	const selectedBroadcastOption = useMemo(() => (
		roomState !== ROOM_STATES.EDITING
			? broadcastOptions[selectedRecordingOption ?? 0]
			: broadcastOptions[2]
	), [selectedRecordingOption, roomState]);

	// get the broadcast state
	const [stateMessage, , , error] = useMemo(() => {
		const ddLog = {
			family: 'BroadcastState',
			event: 'ChangeButtonState',
			location: 'broadcasting-controls.tsx',
			function: 'useMemo',
			serverState,
			previousServerState,
			streamState,
			host,
			hovering: mouseIsOver,
			meeting: meeting?.meeting,
			meeting_id: meeting?.meeting_id,
		};
		appLogger.info('broadcast state 1', ddLog);

		return getBroadcastState(
			mouseIsOver,
			true,
			streamStateRef,
			false,
			ddLog as any,
		);
	}, [
		serverState,
		previousServerState,
		streamState,
		streamStateRef.current,
		mouseIsOver,
		host,
		meeting?.start_time,
		goLiveButtonClicked,
		startingBroadcast,
		stoppingBroadcast,
	]);

	const secondCallback = (time: number) => {
		handleTimeAlerts(time);
	};

	const isLive = useMemo(
		() => serverState === BROADCAST_STARTED && !mockStreamStopping,
		[serverState, mockStreamStopping],
	);

	const goingLive = useMemo(
		() => serverState === BROADCAST_STARTING || mockStreamStarting,
		[serverState, mockStreamStarting],
	);

	const isLiveOrGoingLive = useMemo(() => isLive || goingLive, [isLive, goingLive]);

	const isInitializing = useMemo(
		() => serverState === STARTING_UP || serverState === BROADCAST_PREWARMING,
		[serverState],
	);

	const getMain = useMemo(
		() => (
			isLive
				? (
					<>
						<div className="live-circle" />
						<span>{selectedBroadcastOption.progressLabel ?? ''}</span>
					</>
				) : (
					<>
						<span className="no-select">
							{selectedBroadcastOption.dropdownLabel ?? ''}
						</span>
						<Icon
							name={ICONS.CARROT_DOWN}
							color={
								selectedBroadcastOption.value === 'showbuilder'
									? COLORS.TEXT_GRAY_1200
									: COLORS.TEXT_GRAY_400
							}
							size={9.5}
						/>
					</>
				)
		),
		[isLive, selectedBroadcastOption, mouseIsOver],
	);

	function startBroadcast(clickedFromModal = false) {
		setShowBroadcastModal(false);

		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		if (checkShowCommsOffWarningOrModal(clickedFromModal)) {
			return;
		}

		startStopBroadcast(
			socket,
			mockStreamStarting ? BROADCAST_TEXT.CANCEL : stateMessage,
			false,
			streamStateRef,
			() => {},
			mockStreamStarting,
			() => {},
			(value) => {
				setTimeout(() => {
					setMockStreamStarting(value);
				}, 250);
			},
			startupTimer,
		);

		if (!mockStreamStopping && (
			serverState === BROADCAST_STATES.BROADCAST_STARTED
			|| serverState === BROADCAST_STATES.BROADCAST_STARTING
		)) {
			setMockStreamStopping(true);
		}
	}

	useEffect(() => {
		if (goLiveFromModal) {
			startBroadcast(false);
			dispatch(BroadcastActions.setGoLiveFromModal(false));
		}
	}, [goLiveFromModal]);

	const getButtonIcon = useMemo(() => {
		if (
			(serverState === BROADCAST_STARTING && !mouseIsOver)
			|| (isInitializing && selectedBroadcastOption.value === BROADCAST_OPTIONS.RECORD)
			|| (mockStreamStarting && !mouseIsOver)
		) {
			return (
				<LoadingSpinner
					height={10}
					width={10}
					color={selectedBroadcastOption.value === 'stream' ? '#09090A' : '#FFFFFF'}
					stroke={10}
					style={{ display: 'flex', margin: 'none' }}
				/>
			);
		}

		if (isLiveOrGoingLive && mouseIsOver) {
			return (
				<Icon
					name={ICONS.STREAM_STOP}
					color={selectedBroadcastOption.startIconColor ?? COLORS.BLACK}
					size={12}
				/>
			);
		}

		if (
			(isLiveOrGoingLive && !mouseIsOver)
			|| !selectedBroadcastOption.startIcon
			|| !selectedBroadcastOption.startIconColor
		) {
			return <></>;
		}

		return (
			<Icon
				name={selectedBroadcastOption.startIcon}
				color={selectedBroadcastOption.startIconColor}
				size={12}
			/>
		);
	}, [
		selectedBroadcastOption,
		serverState,
		mouseIsOver,
		isLiveOrGoingLive,
		isInitializing,
		mockStreamStopping,
		mockStreamStarting,
	]);

	const getButtonLabel = useMemo(() => {
		if (!selectedBroadcastOption.startLabel) {
			return 'Showbuilder';
		}

		if (isLiveOrGoingLive && mouseIsOver) {
			return `End ${selectedBroadcastOption.endLabel}`;
		}

		if (isInitializing && selectedBroadcastOption.value === BROADCAST_OPTIONS.RECORD) {
			return 'Initializing';
		}

		switch (serverState) {
			case BROADCAST_STARTED: {
				return mockStreamStopping
					? selectedBroadcastOption.startLabel
					: selectedBroadcastOption.progressLabel;
			}
			case BROADCAST_STARTING: {
				if (!mockStreamStopping && !mouseIsOver) {
					return 'Starting...';
				}
			} /* eslint-disable-next-line no-fallthrough */
			case BROADCAST_STOPPED:
			case READY: {
				if (mockStreamStarting && !mouseIsOver) {
					return 'Starting...';
				}
			} /* eslint-disable-next-line no-fallthrough */
			default: {
				return selectedBroadcastOption?.startLabel ?? '';
			}
		}
	}, [
		selectedBroadcastOption,
		isLiveOrGoingLive,
		mouseIsOver,
		isInitializing,
		serverState,
		mockStreamStarting,
		mockStreamStopping,
	]);

	const startTime = useMemo(() => {
		if (!meeting) return null;

		switch (stateMessage) {
			case BROADCAST_TEXT.REHEARSAL: {
				if (
					nextShowSession.current
					&& nextShowSession.current?.start_time >= meeting.start_time
					&& nextShowSession.current?.end_time < meeting.end_time
				) {
					return nextShowSession.current?.start_time;
				}

				return null;
			}
			case BROADCAST_TEXT.LIVE: {
				return new Date(meeting.broadcast_live_start_time).getTime();
			}
			default:
				return null;
		}
	}, [stateMessage, meeting]);

	function checkShowCommsOffWarningOrModal(clickedFromModal: boolean) {
		if (
			(selectedRecordingOption === RECORDING_OPTIONS.STREAM_AND_RECORD
				|| selectedRecordingOption === RECORDING_OPTIONS.RECORD)
			&& warningModalSent
			&& !clickedFromModal
			&& !backstageCommsActive
			&& stateMessage === BROADCAST_TEXT.GO_LIVE
			&& !isLiveOrGoingLive
		) {
			Alerts.showAlert({
				message: 'Backstage Comms Off',
				description: 'Any backstage audio will be included in your recording.',
				onClick: () => { },
				duration: 4000,
			});
		}

		if (
			!backstageCommsActive
			&& !clickedFromModal
			&& !warningModalSent
			&& !isLiveOrGoingLive
		) {
			setShowBroadcastModal(true);
			dispatch(SessionActions.setWarningModalSent(true));
			return true;
		}

		return false;
	}

	function handleButtonMouseUp(clickedFromModal = false) {
		if (!host) return;
		if (scheduledStream?.startTime && withinUnsafeTimeLeft(scheduledStream.startTime)) {
			return;
		}

		if (
			selectedBroadcastOption.value === BROADCAST_OPTIONS.RECORD
			|| clickedFromModal
			|| (
				selectedBroadcastOption.value === BROADCAST_OPTIONS.STREAM
				&& isLiveOrGoingLive
			)
		) {
			startBroadcast(clickedFromModal);
		} else if (selectedBroadcastOption.value === BROADCAST_OPTIONS.STREAM) {
			dispatch(ModalActions.setModalState({
				modalOpen: true,
				modalTemplate: MODAL_TEMPLATES.LIVE_STREAM_CONFIRMATION,
				modalData: {
					socket,
					initialMode: scheduledStream?.startTime
						&& scheduledStream.startTime.valueOf() > Date.now()
						? LiveStreamStart.Schedule
						: LiveStreamStart.StartNow,
				} as LiveStreamConfirmationModalProps,
			}));
		}
	}

	const goToSettings = () => {
		dispatch(SessionActions.setMetaMenuRoute(META_MENU_ROUTES.EVENT_SETTINGS));
		dispatch(SessionActions.setMetaMenuState(META_MENU_STATES.OPENING));
		setShowBroadcastModal(false);
	};

	const modeLine = useMemo(() => (
		<Portal targetId="mode-line-portal">
			<SwitchTransition mode="in-out">
				<CSSTransition
					key={selectedBroadcastOption.value}
					nodeRef={modeLineRef}
					timeout={250}
					classNames="fade"
					addEndListener={(done) => {
						modeLineRef.current?.addEventListener('transitionend', done, false);
					}}
					unmountOnExit
				>
					<div
						className={classNames(
							'header-mode-line',
							selectedBroadcastOption.value,
						)}
						ref={modeLineRef}
					/>
				</CSSTransition>
			</SwitchTransition>
		</Portal>
	), [selectedBroadcastOption]);

	if (!host && serverState !== BROADCAST_STARTED) {
		return modeLine;
	}

	return (
		<>
			<div
				className={classNames(
					`broadcast-controls-container ${selectedBroadcastOption.value}`,
					{ 'full-button': goingLive || (isLive && mouseIsOver) },
					{ 'is-live': isLive },
					{ error },
					{ 'dropdown-open': popoverStatus === PopoverStatus.Open },
				)}
				onMouseEnter={() => setMouseIsOver(host)}
				onMouseLeave={() => setMouseIsOver(false)}
				style={{ pointerEvents: !host ? 'none' : 'auto' }}
				data-testid='broadcast-controls-container'
			>
				{modeLine}
				<div
					className='broadcast-controls-main'
					onMouseDown={() => {
						if (
							serverState === BROADCAST_STARTING
							|| serverState === BROADCAST_STARTED
							|| mockStreamStarting
							|| mockStreamStopping
						) {
							return;
						}
						setDropdownIsOpen(true);
					}}
					data-testid='broadcast-controls-main'
				>
					{getMain}
				</div>
				{selectedBroadcastOption.value !== 'showbuilder' && (
					<div
						className={classNames(
							'broadcast-controls-button',
							{
								initializing: isInitializing
								&& selectedBroadcastOption.value === BROADCAST_OPTIONS.RECORD,
							},
							{ ending: mockStreamStopping },
						)}
						onMouseUp={() => selectedBroadcastOption.value !== 'showbuilder' && handleButtonMouseUp(false)}
						data-testid='broadcast-controls-button'
					>
						{getButtonIcon}
						{(!isLive || mouseIsOver) && (
							<span>{getButtonLabel}</span>
						)}
						{isLive && (
							<LiveCounter
								notLive={false}
								isHeader={true}
								isHost={host}
								startTime={startTime}
								secondCallback={secondCallback}
							/>
						)}
					</div>
				)}
				<BroadcastDropdown
					socket={socket}
					roomState={roomState}
					selectedBroadcastOption={selectedBroadcastOption}
					isOpen={dropdownIsOpen}
					onClose={() => setDropdownIsOpen(false)}
					setPopoverStatus={setPopoverStatus}
				/>
			</div>
			{showBroadcastModal && (
				<BroadcastModal
					broadcastOption={selectedBroadcastOption.value}
					goToSettings={goToSettings}
					handleClick={(opt: boolean) => {
						setMouseIsOver(false);
						handleButtonMouseUp(opt);
					}}
					icon={getButtonIcon}
					text={selectedBroadcastOption.startLabel ?? ''}
				/>
			)}
		</>
	);
}
